import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'

export interface TareaNovedad {
  id: string
  titulo: string
  created_at: string | null
  cliente_nombre: string | null
  asignado_a: string | null
}

export interface ClienteNovedad {
  /** id del pivot cliente_responsables. Un cliente puede tener VARIOS responsables
      (UNIQUE(cliente_id, usuario_id)), así que cliente_id no identifica la fila. */
  id: string
  cliente_id: string
  created_at: string | null
  cliente_nombre: string | null
  responsable_nombre: string | null
}

/** Fila cruda de tareas con el join anidado a cliente/persona. */
type TareaRow = {
  id: string
  titulo: string
  created_at: string | null
  asignado_a: string | null
  cliente: { persona: { nombre_completo: string } | null } | null
}

const SELECT_TAREA =
  'id, titulo, created_at, asignado_a, cliente:clientes(persona:personas(nombre_completo))'

const mapTarea = (t: TareaRow): TareaNovedad => ({
  id: t.id,
  titulo: t.titulo,
  created_at: t.created_at,
  asignado_a: t.asignado_a,
  cliente_nombre: t.cliente?.persona?.nombre_completo ?? null,
})

/**
 * ORR-217 v1: fuente de datos de la card "Novedades" del homepage — 3 de los 4 ítems
 * (el 4to, vencimientos de hoy, reusa el scoping por responsable de ProximosVencimientosCard
 * directamente en el componente; ver docs/plans/orr217-novedades-homepage.md).
 *
 * `usuarioId = null` significa ADMIN: el dueño del estudio no se asigna a sí mismo como
 * responsable de clientes, así que filtrar por "lo mío" le dejaba 2 de las 4 secciones
 * permanentemente vacías (medido en el probe de DEV). Para admin el alcance es el estudio entero.
 * El 5to ítem del pedido original ("actualizaciones de menta") queda fuera — D-17 en
 * tasks/decisiones.md, es una decisión de producto, no de implementación.
 */
export const novedadesService = {
  /** Últimas tareas asignadas a mí (organización + email del asignado). */
  async tareasAsignadasAMi(
    client: SupabaseClient<Database>,
    organizacionId: string,
    email: string,
    limite = 5,
  ): Promise<TareaNovedad[]> {
    const { data, error } = await client
      .from('tareas')
      .select(SELECT_TAREA)
      .eq('organizacion_id', organizacionId)
      .eq('asignado_a', email)
      .order('created_at', { ascending: false })
      .limit(limite)

    if (error) throw error
    return ((data ?? []) as unknown as TareaRow[]).map(mapTarea)
  },

  /**
   * Tareas asignadas a OTRO colaborador. Admin (`usuarioId = null`): todas las de la
   * organización. Resto: solo las de MIS clientes (`cliente_responsables.usuario_id = yo`),
   * en dos queries porque tareas no tiene FK directa a cliente_responsables.
   */
  async tareasAsignadasAOtros(
    client: SupabaseClient<Database>,
    organizacionId: string,
    usuarioId: string | null,
    email: string,
    limite = 5,
  ): Promise<TareaNovedad[]> {
    // Los clientes propios se resuelven ANTES de armar el query de tareas: sin ninguno, la
    // sección está vacía por definición y no hay por qué construir la segunda consulta.
    let clienteIds: string[] | null = null
    if (usuarioId) {
      const { data: misClientes, error: errorClientes } = await client
        .from('cliente_responsables')
        .select('cliente_id')
        .eq('usuario_id', usuarioId)

      if (errorClientes) throw errorClientes
      clienteIds = (misClientes ?? []).map((c) => c.cliente_id)
      if (clienteIds.length === 0) return []
    }

    let query = client
      .from('tareas')
      .select(SELECT_TAREA)
      .eq('organizacion_id', organizacionId)
      .not('asignado_a', 'is', null)
      .neq('asignado_a', email)
      .order('created_at', { ascending: false })
      .limit(limite)

    if (clienteIds) query = query.in('cliente_id', clienteIds)

    const { data, error } = await query
    if (error) throw error
    return ((data ?? []) as unknown as TareaRow[]).map(mapTarea)
  },

  /**
   * Últimos clientes con responsable asignado. Admin (`usuarioId = null`): los del estudio,
   * con el nombre de a quién se asignó. Resto: solo los asignados a mí.
   * El filtro por organización va sobre el cliente — cliente_responsables no tiene esa columna.
   */
  async clientesNuevosAsignados(
    client: SupabaseClient<Database>,
    organizacionId: string,
    usuarioId: string | null,
    limite = 5,
  ): Promise<ClienteNovedad[]> {
    let query = client
      .from('cliente_responsables')
      .select(
        'id, cliente_id, created_at, cliente:clientes!inner(organizacion_id, persona:personas(nombre_completo)), responsable:usuarios_brainhub(nombre, apellido, email)',
      )
      .eq('cliente.organizacion_id', organizacionId)
      .order('created_at', { ascending: false })
      .limit(limite)

    if (usuarioId) query = query.eq('usuario_id', usuarioId)

    const { data, error } = await query
    if (error) throw error

    return ((data ?? []) as unknown as Array<{
      id: string
      cliente_id: string
      created_at: string | null
      cliente: { persona: { nombre_completo: string } | null } | null
      responsable: { nombre: string | null; apellido: string | null; email: string } | null
    }>).map((c) => {
      const r = c.responsable
      const nombreCompleto = [r?.nombre, r?.apellido].filter(Boolean).join(' ').trim()
      return {
        id: c.id,
        cliente_id: c.cliente_id,
        created_at: c.created_at,
        cliente_nombre: c.cliente?.persona?.nombre_completo ?? null,
        responsable_nombre: nombreCompleto || r?.email || null,
      }
    })
  },
}
